import React, { useState, useEffect } from 'react';
import { cva } from 'class-variance-authority';
import clsx from 'clsx';
import { motion, useReducedMotion, type HTMLMotionProps } from 'motion/react';
import { Icon } from './Icon';
import { entranceSpring } from './primitives/motion';
import styles from './Avatar.module.css';

// ── CVA ─────────────────────────────────────────────────────────────────────

const avatar = cva(styles.base);

// ── Types ─────────────────────────────────────────────────────────────────────

type AvatarShape = 'circle' | 'square';
type AvatarSize = 'sm' | 'md' | 'lg' | number;

type SpanMotionSafeProps = Omit<
  React.HTMLAttributes<HTMLSpanElement>,
  'onDrag' | 'onDragEnd' | 'onDragStart' | 'onAnimationStart' | 'onAnimationEnd' | 'onAnimationIteration'
>;

interface AvatarProps extends SpanMotionSafeProps {
  src?: string;
  alt?: string;
  size?: AvatarSize;
  shape?: AvatarShape;
  icon?: React.ReactNode;
  gap?: number;
  children?: React.ReactNode;
}

const sizeMap: Record<string, number> = { sm: 24, md: 32, lg: 40 };

function resolveSize(s: AvatarSize): number {
  return typeof s === 'number' ? s : (sizeMap[s] ?? 32);
}

/**
 * Avatar — user image, icon, or initials.
 *
 * | Prop | Type | Default | Description |
 * |------|------|---------|-------------|
 * | src | string | — | Image URL |
 * | alt | string | — | Image alt |
 * | size | 'sm' \| 'md' \| 'lg' \| number | 'md' | Diameter |
 * | shape | 'circle' \| 'square' | 'circle' | Shape |
 * | icon | ReactNode | User icon | Fallback icon |
 * | gap | number | 4 | Side padding for text |
 *
 * Sub: Avatar.Group
 *
 * @example
 * <Avatar src="user.jpg" />
 * <Avatar>BN</Avatar>
 * <Avatar icon={<Bot size={16} />} />
 *
 * Motion: Avatar springs in on mount with the shared `entranceSpring` preset.
 * To get the exit animation when consumers remove avatars from a list (e.g.
 * a presence stack), wrap the mapped output with `<AnimatePresence>` at the
 * call site:
 *
 *     <AnimatePresence>
 *       {users.map(u => <Avatar key={u.id}>{u.initials}</Avatar>)}
 *     </AnimatePresence>
 *
 * Without AnimatePresence at the call site, only the entrance animation is
 * observable — React unmounts the Avatar synchronously on removal.
 */
export function Avatar({
  src,
  alt = '',
  size = 'md',
  shape = 'circle',
  icon,
  gap = 4,
  children,
  style,
  className,
  ...props
}: AvatarProps) {
  const [imgError, setImgError] = useState(false);
  const shouldReduce = useReducedMotion() ?? false;

  // Reset error state when src changes
  useEffect(() => {
    setImgError(false);
  }, [src]);

  const px = resolveSize(size);
  const fontSize = px * 0.4;

  // shape → borderRadius: runtime-computed physical fact (component-specific dimension)
  const borderRadius = shape === 'circle' ? 'var(--radius-full)' : 'var(--radius-sm)';
  // backgroundColor: runtime state (depends on src + imgError)
  const backgroundColor = src && !imgError ? 'transparent' : 'var(--surface-container-high)';

  // Honor `prefers-reduced-motion` — omit the spring entirely when set.
  const motionProps: HTMLMotionProps<'span'> = shouldReduce ? {} : entranceSpring;

  return (
    <motion.span
      {...motionProps}
      className={avatar({ className })}
      // Size (px) and borderRadius are runtime-computed from props — physical dimensions
      // backgroundColor is runtime state (src validity) — cannot be CSS-only
      // These are justified per component-styling.md §Core-ui primitives
      // eslint-disable-next-line nula-style/no-inline-style -- runtime physical dimensions and state
      style={{
        width: px,
        height: px,
        borderRadius,
        backgroundColor,
        color: 'var(--on-surface-variant)',
        fontSize,
        fontWeight: 600,
        letterSpacing: '0.02em',
        ...style,
      }}
      {...props}
    >
      {src && !imgError ? (
        <img
          src={src}
          alt={alt}
          onError={() => setImgError(true)}
          className={styles.img}
        />
      ) : children ? (
        <span
          className={styles.textContent}
          // gap is a runtime prop value (number → px) — physical dimension
          // eslint-disable-next-line nula-style/no-inline-style -- gap prop is a runtime physical dimension
          style={{ padding: `0 ${gap}px` }}
        >
          {children}
        </span>
      ) : (
        (icon ?? <Icon name="person" size={px * 0.5} aria-hidden="true" />)
      )}
    </motion.span>
  );
}

// ── Avatar.Group ─────────────────────────────────────────────────────────────

interface AvatarGroupProps extends React.HTMLAttributes<HTMLDivElement> {
  maxCount?: number;
  size?: AvatarSize;
  children: React.ReactNode;
}

function AvatarGroup({ maxCount, size, children, style, className, ...props }: AvatarGroupProps) {
  const items = React.Children.toArray(children);
  const visible = maxCount !== undefined ? items.slice(0, maxCount) : items;
  const overflow = maxCount !== undefined ? items.length - maxCount : 0;
  const px = resolveSize(size ?? 'md');

  return (
    <div className={clsx(styles.group, className)} style={style} {...props}>
      {visible.map((child, i) => (
        <span
          key={i}
          className={styles.groupItem}
          // marginLeft and zIndex are runtime-computed from avatar size — physical layout
          // eslint-disable-next-line nula-style/no-inline-style -- overlap layout is runtime-computed
          style={{ marginLeft: i > 0 ? -(px * 0.6) : 0, zIndex: visible.length - i }}
        >
          {React.isValidElement(child)
            ? React.cloneElement(child as React.ReactElement<AvatarProps>, { size: size ?? (child.props as AvatarProps).size })
            : child}
        </span>
      ))}
      {overflow > 0 && (
        <span
          className={styles.groupItem}
          // eslint-disable-next-line nula-style/no-inline-style -- overlap layout is runtime-computed
          style={{ marginLeft: -(px * 0.6), zIndex: 0 }}
        >
          <Avatar
            size={size ?? 'md'}
            // eslint-disable-next-line nula-style/no-inline-style -- overflow font-size is runtime-computed from avatar size (px * 0.35)
            style={{ backgroundColor: 'var(--outline-variant)', color: 'var(--on-surface)', fontSize: px * 0.35 }}
          >
            +{overflow}
          </Avatar>
        </span>
      )}
    </div>
  );
}

Avatar.Group = AvatarGroup;
